<script lang="ts">
    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import { browser } from '$app/environment';
    import { dev } from '$app/environment';

    const dispatch = createEventDispatcher();

    // デバッグログ用のヘルパー関数
    function debugLog(...args: any[]) {
        if (dev) {
            console.log(...args);
        }
    }

    // エラーログ用のヘルパー関数（エラーは本番でも出力）
    function errorLog(...args: any[]) {
        console.error(...args);
    }

    export let videoDetails: any;
    export let channelData: any;
    export let embedDomain: string = '';
    export let isMobile: boolean = false;
    export let videoContainer: HTMLElement | null = null;

    let videoHeight: number;
    let videoTime = 0;
    let videoTimeMs = 0;
    let lastSeekTime = 0;
    let initialSeekComplete = false;
    let playerTimeTracker: ReturnType<typeof setInterval> | null = null;
    let iframeElement: HTMLIFrameElement | null = null;
    let isPlayerInitialized = false;

    // 動画の再生位置をローカルストレージに保存する関数
    function saveVideoProgress(videoId: string, currentTime: number) {
        if (!browser || !videoId || currentTime <= 0) return;
        
        try {
            const videoProgressKey = 'videoProgress';
            let videoProgressMap: Record<string, number> = {};
            
            const storedProgress = localStorage.getItem(videoProgressKey);
            if (storedProgress) {
                videoProgressMap = JSON.parse(storedProgress);
            }
            
            if (Math.abs((videoProgressMap[videoId] || 0) - currentTime) >= 5) {
                videoProgressMap[videoId] = currentTime;
                localStorage.setItem(videoProgressKey, JSON.stringify(videoProgressMap));
            }
        } catch (error) {
            errorLog('再生位置の保存中にエラーが発生しました:', error);
        }
    }

    // ローカルストレージから動画の再生位置を読み込む関数
    function loadVideoProgress(videoId: string): number {
        if (!browser || !videoId) return 0;
        
        try {
            const videoProgressKey = 'videoProgress';
            const storedProgress = localStorage.getItem(videoProgressKey);
            
            if (storedProgress) {
                const videoProgressMap: Record<string, number> = JSON.parse(storedProgress);
                const savedTime = videoProgressMap[videoId];
                
                if (savedTime && savedTime > 0) {
                    return savedTime;
                }
            }
        } catch (error) {
            errorLog('再生位置の読み込み中にエラーが発生しました:', error);
        }
        
        return 0; // デフォルトは0秒から再生
    }

    // YouTubeプレーヤーAPIの初期化
    function initializeYouTubePlayer() {
        if (!browser) return;
        
        const playerId = isMobile ? 'youtube-player-mobile' : 'youtube-player';
        const youtubeIframe = document.querySelector(`iframe#${playerId}`) as HTMLIFrameElement;
        
        if (!youtubeIframe) {
            errorLog(`YouTubeプレーヤーiframe（${playerId}）が見つかりません`);
            return;
        }
        
        debugLog(`YouTubeプレーヤーAPI（${playerId}）を初期化します`);

        // 既存のイベントリスナーとタイマーをクリア
        cleanup();
        
        // イベントリスナーを追加
        window.addEventListener('message', handlePostMessage);

        // プレーヤーの初期化シーケンス
        function initializeSequence() {
            if (!youtubeIframe.contentWindow) return;
            
            // リスニングモードを有効化
            youtubeIframe.contentWindow.postMessage(JSON.stringify({
                event: 'listening',
                id: youtubeIframe.id
            }), '*');

            // プレーヤーの状態を要求
            youtubeIframe.contentWindow.postMessage({
                event: 'command',
                func: 'getPlayerState',
                id: youtubeIframe.id
            }, '*');

            // 現在の時間を要求
            youtubeIframe.contentWindow.postMessage({
                event: 'command',
                func: 'getCurrentTime',
                id: youtubeIframe.id
            }, '*');

            // 保存された再生位置があれば、そこにシークする
            if (videoDetails && videoDetails.id && videoDetails.live_broadcast_content !== 'live' && !initialSeekComplete) {
                const savedTime = loadVideoProgress(videoDetails.id);
                if (savedTime > 0) {
                    setTimeout(() => {
                        seekTo(savedTime);
                        initialSeekComplete = true;
                    }, 2000); // 2秒後にシーク（プレーヤーの読み込みを待つ）
                }
            }
        }

        // 初期化を試行
        initializeSequence();
        
        // プレーヤーの監視を開始
        playerTimeTracker = setInterval(() => {
            if (!youtubeIframe.contentWindow) return;
            
            // プレーヤーが初期化されていない場合は再試行
            if (!isPlayerInitialized) {
                debugLog('プレーヤーの初期化を再試行します...');
                initializeSequence();
                return;
            }

            if (videoDetails?.platform === 'youtube' && 
                videoDetails.live_broadcast_content !== 'live') {
                
                // 現在の時間を要求
                youtubeIframe.contentWindow.postMessage({
                    event: 'command',
                    func: 'getCurrentTime',
                    id: youtubeIframe.id
                }, '*');

                // 時間差をチェック
                const timeDiff = Math.abs(videoTime - lastSeekTime);
                if (timeDiff > 1) {
                    dispatch('chatSync', { 
                        time: videoTime, 
                        timeMs: videoTimeMs 
                    });
                    lastSeekTime = videoTime;

                    // 再生位置を保存
                    saveVideoProgress(videoDetails.id, videoTime);
                }
            }
        }, 500);
    }

    // メッセージハンドラ
    function handlePostMessage(event: MessageEvent) {
        try {
            // reCAPTCHAメッセージは無視
            if (typeof event.data === 'string' && event.data.includes('_grecaptcha_')) {
                return;
            }

            let data;
            if (typeof event.data === 'string') {
                try {
                    data = JSON.parse(event.data);
                } catch (e) {
                    errorLog('JSONパースエラー:', e);
                    return;
                }
            } else {
                data = event.data;
            }
            
            // プレーヤーの準備完了を検出
            if (data.event === 'onReady' || 
                (data.event === 'infoDelivery' && data.info && data.info.playerState === 1)) {
                isPlayerInitialized = true;
            }

            // 時間情報の更新
            if (data.event === 'infoDelivery' && data.info) {
                // 再生状態の変化を検出
                if (data.info.playerState !== undefined) {
                    dispatch('stateChange', { state: data.info.playerState });
                    
                    if (data.info.playerState === 0) { // 動画再生終了
                        debugLog('動画再生が終了しました');
                        // 動画が終了したら再生位置をリセット
                        if (videoDetails && videoDetails.id) {
                            saveVideoProgress(videoDetails.id, 0);
                        }
                    }
                }

                // 現在の再生位置情報
                if (data.info.currentTime !== undefined) {
                    const currentTimeSec = data.info.currentTime;
                    
                    // 時間を更新
                    videoTime = currentTimeSec;
                    videoTimeMs = Math.floor(currentTimeSec * 1000);
                    
                    // 時間の更新を通知
                    dispatch('timeUpdate', { 
                        time: videoTime, 
                        timeMs: videoTimeMs 
                    });

                    // 定期的に再生位置を保存
                    if (videoDetails && videoDetails.id) {
                        saveVideoProgress(videoDetails.id, currentTimeSec);
                    }
                }
            }
        } catch (e) {
            errorLog('メッセージ処理中にエラー:', e);
        }
    }

    // クリーンアップ関数
    function cleanup() {
        if (browser) {
            window.removeEventListener('message', handlePostMessage);
            if (playerTimeTracker) {
                clearInterval(playerTimeTracker);
                playerTimeTracker = null;
            }
        }
    }

    // コンポーネントのクリーンアップ
    onDestroy(() => {
        cleanup();
    });

    onMount(() => {
        if (browser && videoDetails?.platform === 'youtube') {
            // プレーヤーの初期化を遅延実行
            setTimeout(() => {
                initializeYouTubePlayer();
            }, 1000);
        }
    });

    // 外部からシークできるようにメソッドを公開
    export function seekTo(seconds: number) {
        if (!browser) return;
        
        // 現在のレイアウトに応じて適切なプレーヤーIDを使用
        const playerId = isMobile ? 'youtube-player-mobile' : 'youtube-player';
        debugLog(`現在のレイアウト: ${isMobile ? 'モバイル' : 'デスクトップ'}, 使用するID: ${playerId}`);
        
        const youtubeIframe = document.querySelector(`iframe#${playerId}`) as HTMLIFrameElement;
        
        if (!youtubeIframe || !youtubeIframe.contentWindow) {
            errorLog(`YouTubeプレーヤーiframe（${playerId}）が見つかりません`);
            return;
        }
        
        debugLog(`${seconds}秒にシークします（${playerId}）`);
        
        // YouTubeプレーヤーに複数の形式でシークコマンドを送信（互換性のため）
        // 標準的なYouTube IFrame API形式
        youtubeIframe.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'seekTo',
            args: [seconds, true], // true = シーク後に再生を開始
            id: youtubeIframe.id
        }), '*');
        
        // 代替形式も送信（一部の実装で必要）
        youtubeIframe.contentWindow.postMessage({
            event: 'command',
            func: 'seekTo',
            args: [seconds, true],
            id: youtubeIframe.id
        }, '*');
        
        // さらに別の形式も送信
        youtubeIframe.contentWindow.postMessage({
            'event': 'infoDelivery',
            'info': { 'currentTime': seconds }
        }, '*');
        
        // 直接再生位置パラメータも送信
        youtubeIframe.contentWindow.postMessage({
            'videoCurrentTime': seconds
        }, '*');
        
        // シーク時間を通知
        dispatch('seek', { time: seconds, timeMs: Math.floor(seconds * 1000) });
    }

    // 現在の再生位置を取得
    export function getCurrentTime() {
        return { 
            seconds: videoTime,
            milliseconds: videoTimeMs
        };
    }
</script>

<div bind:this={videoContainer} class="aspect-w-16 aspect-h-9 mb-0 overflow-hidden">
    {#if videoDetails.platform === 'twitch'}
        {#if videoDetails.live_broadcast_content === 'live'}
            <iframe
                src="https://player.twitch.tv/?channel={channelData?.twitch_id}&muted=false&parent={embedDomain}"
                frameborder="0"
                allowfullscreen
                scrolling="no"
                height="100%"
                width="100%"
                class="w-full h-full"
                title="Twitch Live Stream"
            ></iframe>
        {:else}
            <iframe
                src="https://player.twitch.tv/?video={videoDetails.id}&parent={embedDomain}&autoplay=true"
                frameborder="0"
                allowfullscreen
                scrolling="no"
                height="100%"
                width="100%"
                class="w-full h-full"
                title="Twitch Video"
            ></iframe>
        {/if}
    {:else if videoDetails.platform === 'youtube'}
        <iframe
            id={isMobile ? 'youtube-player-mobile' : 'youtube-player'}
            src="https://www.youtube.com/embed/{videoDetails.id}?autoplay=1&controls=1&enablejsapi=1&origin={browser ? window.location.origin : ''}&widgetid=1"
            title={videoDetails.title}
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            height="100%"
            width="100%"
            class="w-full h-full {isMobile ? 'lg:hidden' : 'lg:block hidden'}"
        ></iframe>
    {/if}
</div>

<style>
    /* アスペクト比を維持するためのスタイル */
    .aspect-w-16 {
        position: relative;
        padding-bottom: 56.25%; /* 16:9 のアスペクト比 */
    }
    .aspect-w-16 iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
</style>